import { useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { DialogContent } from "@mui/material"
import { Form, Formik, FormikProps } from "formik"
import { TimelineCalendarEventTimeRange } from "../../createOrder/components/resource/calendar/fullCalendarTypes"
import {
    BookingConfig,
    DynamicConfigField,
    DynamicConfigKey,
    ExtendedDynamicConfigKey,
} from "../../createOrder/components/form/formTypes"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { generateBookingValidationSchema } from "../../createOrder/components/form/formLibBooking"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { ResourceItem, SelectedMainUserState } from "../../createOrder/createOrderTypes"
import { MeetingDetailsFormPart } from "./meetingDetails/MeetingDetailsFormPart"
import { AttendeesFormPart } from "./attendees/AttendeesFormPart"
import { StandardDialog } from "../StandardDialog"

const useStyles = makeStyles()(() => ({
    dialogContent: {
        padding: "3rem",
        paddingTop: "1.5rem",
        display: "flex",
        gap: "3rem",
        flexDirection: "column",
        alignItems: "center",
    },
}))

type ConfirmBookingDialogProps = Readonly<{
    open: boolean
    onClose: (formValues?: Record<ExtendedDynamicConfigKey | string, any>) => void
    config: BookingConfig
    initialValues: Record<ExtendedDynamicConfigKey | string, any>
    handleSubmit: (values: Record<ExtendedDynamicConfigKey | string, any>) => void
    resourcesList: ResourceItem[]
    timeRange?: TimelineCalendarEventTimeRange
    handleClearResourceIds: () => void
    user: SelectedMainUserState
    orderId?: number | null
}>

export function ConfirmBookingDialog({
    open,
    onClose,
    config,
    initialValues,
    handleSubmit,
    resourcesList,
    timeRange,
    handleClearResourceIds,
    user,
    orderId,
}: ConfirmBookingDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [step, setStep] = useState(1)
    const formikRef = useRef<FormikProps<Record<DynamicConfigKey | string, any>>>(null)

    const allFields: DynamicConfigField[] = config.sections.flatMap((section) =>
        section.groups.flatMap((group) => group.fields),
    )

    const shouldShowAttendeesStep =
        allFields.some((f) => f.key === DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE) &&
        formikRef?.current?.values?.[DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE] === true

    const validationSchema = generateBookingValidationSchema(config, l10n)

    const handleBackClick = () => {
        setStep(1)
    }

    const handleConfirmClick = (isShowParticipants: boolean) => {
        isShowParticipants ? setStep(2) : formikRef.current?.submitForm()
    }

    const handleCloseWithParams = () => {
        if (formikRef?.current) {
            const formValues = formikRef?.current?.values
            onClose(formValues)
            return
        }
        onClose()
    }

    return (
        <StandardDialog
            dialogTitle={
                step === 1 ? (
                    orderId ? (
                        <LocalizedStrict
                            id="create-order-confirm-booking-update-meeting-details"
                            vars={{ id: `${orderId}` }}
                        >
                            <>Update meeting details #{orderId}</>
                        </LocalizedStrict>
                    ) : (
                        <LocalizedStrict id="create-order-confirm-booking-meeting-details">
                            <>Meeting details</>
                        </LocalizedStrict>
                    )
                ) : shouldShowAttendeesStep ? (
                    <LocalizedStrict id="create-order-confirm-booking-dialog-add-attendees">
                        <>Add participants</>
                    </LocalizedStrict>
                ) : undefined
            }
            open={open}
            onClose={handleCloseWithParams}
            maxWidthProp="lg"
            scroll="body"
            handleBackButton={step === 1 ? undefined : handleBackClick}
            backButtonText={l10n.getString("create-order-confirm-booking-dialog-back-button")}
        >
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                <Form>
                    <DialogContent className={classes.dialogContent}>
                        {step === 1 ? (
                            <MeetingDetailsFormPart
                                config={config}
                                handleClick={handleConfirmClick}
                                resourcesList={resourcesList}
                                timeRange={timeRange}
                                handleClearResourceIds={handleClearResourceIds}
                                user={user}
                            />
                        ) : shouldShowAttendeesStep ? (
                            <AttendeesFormPart />
                        ) : null}
                    </DialogContent>
                </Form>
            </Formik>
        </StandardDialog>
    )
}
